/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { subscriptionService, paymentService, analyticsService } from '../services';
import { PlanDetails, PlanType, Currency, ServiceType } from '../types/api';
import { toast } from 'react-hot-toast';
import { ShoppingCart, Wallet, Gift, AlertCircle, Flower2, Cherry, Leaf, ArrowLeft, Sprout } from 'lucide-react';
import { getStripePriceId } from '../config/stripe';
import { startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../utils/priceFormat';
import { checkAndForceRefresh } from '../utils/versionCheck';
import PaymentErrorDisplay from '../components/ui/PaymentErrorDisplay';
import { formatErrorForDisplay } from '../utils/paymentErrorHandler';
import { FEATURES } from '../config';
import ShutdownNotice from '../components/MaintenanceNotice';

// Import custom hooks
import { useAddressContext } from '../context/AddressContext';
import { useCurrentUser } from '../hooks/useCurrentUser';
import addressService, { AddressWithId } from '../services/addressService';
import { usePromotionCode } from '../hooks/usePromotionCode';

// Import Zustand stores and selectors
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { 
  useSubscriptionOptions, 
  useUIState, 
  usePlanDetails, 
  useGiftState, 
  useScheduleState,
  usePromotionState,
  useToastFlags,
  usePriceCalculation,
  useCurrencyState
} from '../stores/subscriptionSelectors';

// Import components directly without lazy loading
import GiftHeader from '../components/subscription/GiftHeader';
import RecipientInfoSection from '../components/subscription/RecipientInfoSection';
import SubscriptionPlanSection from '../components/subscription/SubscriptionPlanSection';
import PaymentMethodsSection from '../components/subscription/PaymentMethodsSection';
import OrderSummarySection from '../components/subscription/OrderSummarySection';
import SubscribeButton from '../components/subscription/SubscribeButton';
import AddressAndScheduleSection from '../components/subscription/AddressAndScheduleSection';
import AddressManagementModal from '../components/AddressManagementModal';

// Import vase image
import vaseImage from '../assets/images/flowers/vase.png';

// Import scissors image
import scissorsImage from '../assets/images/scissors.png';

// Import cryptoPaymentService
import cryptoPaymentService from '../services/cryptoPaymentService';
import { CurrencySection } from '../components/subscription/CurrencySection';

// Import configuration service for pricing
import configService from '../services/configService';

// Import the Facebook event service
import { 
  sendInitiateCheckoutEvent, 
  sendSubscribeEvent 
} from '../services';

// Import product ID utilities
import { generateProductId, generateProductName, generateProductCategory, generateContentIds } from '../utils/productIdUtils';

// Import extracted utility functions
import { getOriginalPriceFromConfig, getTrialPriceFromConfig, getCryptoPriceFromConfig } from '../utils/subscriptionPriceUtils';
import { selectBestAddressFrom } from '../utils/subscriptionAddressUtils';
import { VALID_PLAN_TYPES, PROMO_CODE_STORAGE_KEY } from '../utils/subscriptionConstants';

// Import extracted components
import { AddressSelectionPrompt } from '../components/subscription/AddressSelectionPrompt';
import { OptimizedAddressSection } from '../components/subscription/OptimizedAddressSection';
import ServiceTypeSection from '../components/subscription/ServiceTypeSection';
import FAQSection from '../components/subscription/FAQSection';
import BillingPeriodSection from '../components/subscription/BillingPeriodSection';
import SubscriptionDetailsInfo from '../components/subscription/SubscriptionDetailsInfo';

// Import custom hooks
import { useFacebookBrowserDetection } from '../hooks/useFacebookBrowserDetection';
import { useSubscriptionValidation } from '../hooks/useSubscriptionValidation';
import { useSubscriptionTracking } from '../hooks/useSubscriptionTracking';
import { useSubscriptionCreation } from '../hooks/useSubscriptionCreation';
import { usePaymentProcessing } from '../hooks/usePaymentProcessing';
import { usePromotionHandling } from '../hooks/usePromotionHandling';

// Import new user gift utilities and component
import { isEligibleForNewSubscriberGift } from '../utils/newUserUtils';
import NewUserGiftSection from '../components/subscription/NewUserGiftSection';








export default function Subscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Use Facebook browser detection hook
  const { isFacebookBrowser, isButtonVisible, normalButtonRef } = useFacebookBrowserDetection();
  
  // Use validation and tracking hooks
  const { validateUserAuthentication, validateAddress, validateGiftInformation } = useSubscriptionValidation();
  const { trackBeginCheckout, trackInitiateCheckout, trackSubscriptionEvents } = useSubscriptionTracking();
  const { createSubscription } = useSubscriptionCreation();
  const { processPayment } = usePaymentProcessing();
  const { handlePromotionCode } = usePromotionHandling();
  
  // Use the AddressContext instead of the AddressManager hook
  const { 
    addresses, 
    selectedAddress, 
    isLoading: addressLoading, 
    error: addressError,
    selectAddress,
    refreshAddresses
  } = useAddressContext();

  // Version check for subscription page - shows gentle notification if update needed
  useEffect(() => {
    // Check version on subscription page load - shows user-friendly notification if needed
    checkAndForceRefresh();
    
    // Additional check when user returns to tab (for subscription page specifically)
    const handleFocus = () => {
      checkAndForceRefresh();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);


  
  // Get translation function
  const { t } = useTranslation();
  
  // Get the current user to access email
  const { user: currentUser } = useCurrentUser();
  
  // Check authentication status when component mounts - but don't force redirect
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    
    // If we have a token but no user data, attempt to refresh user data
    if (token && !currentUser) {
      // This will trigger the user data fetch via the CurrentUserProvider
      // The provider will handle authentication errors if the token is invalid
    }
  }, [currentUser]);
  

  
  // Extract promotion code from URL params, location state, or localStorage
  const initialPromotionCode = searchParams.get('promo') || 
                         location.state?.promotionCode || 
                         localStorage.getItem(PROMO_CODE_STORAGE_KEY);
  
  // Add service type state
  const [serviceType, setServiceType] = useState<ServiceType>('flower');
  
  // Get state from Zustand store using selectors
  const {
    subscriptionType, subscriptionLevel, paymentMethod: selectedPaymentMethod, paymentType,
    setSubscriptionType, setSubscriptionLevel, setPaymentMethod, setPaymentType, updateSubscriptionState,
    setShowPromptPayInfo
  } = useSubscriptionOptions();
  
  const {
    loading, error, showPromptPayInfo, showAddressModal, showManualAddressPrompt,
    setLoading, setError, setShowAddressModal, setShowManualAddressPrompt
  } = useUIState();
  
  // Payment error state for detailed error display
  const [paymentError, setPaymentError] = useState<any>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const { planDetails, setPlanDetails } = usePlanDetails();
  
  const {
    isGift, recipientName, recipientPhone, giftMessage, senderName, senderPhone,
    updateGiftState
  } = useGiftState();
  
  const {
    selectedTimeSlots, selectedDate, weeklyDay,
    updateScheduleState
  } = useScheduleState();
  
  const {
    promotionCode, promotionDetails,
    setPromotionCode, setPromotionDetails
  } = usePromotionState();
  
  const {
    hasShownScheduleToast, hasShownPromoToast, hasShownPaymentSwitchToast,
    setHasShownScheduleToast, setHasShownPromoToast, setHasShownPaymentSwitchToast
  } = useToastFlags();
  
  // Use refs for tracking state that shouldn't trigger re-renders
  const addressSelectionAttempted = useRef(false);
  const addressLoadingInProgress = useRef(false);
  
  // Initialize promotion code from URL/localStorage
  useEffect(() => {
    if (initialPromotionCode && !promotionCode) {
      setPromotionCode(initialPromotionCode);
    }
  }, [initialPromotionCode, promotionCode, setPromotionCode]);
  
  // Set gift mode and service type based on location state
  useEffect(() => {
    if (location.state?.giftType === 'direct') {
      // Update the isGift state in the store
      useSubscriptionStore.setState({ isGift: true });
    } else if (location.state?.clearGiftMode === true || location.state?.isGift === false || location.state?.giftType === null) {
      // Explicitly clear gift mode when coming from regular subscription pages
      useSubscriptionStore.setState({ isGift: false });
    }
    
    // Set service type if provided in location state
    if (location.state?.serviceType) {
      setServiceType(location.state.serviceType);
    }
  }, [location.state?.giftType, location.state?.serviceType, location.state?.clearGiftMode, location.state?.isGift]);

  // Set subscription level based on selected plan from location state
  useEffect(() => {
    if (location.state?.selectedPlan) {
      const selectedPlan = location.state.selectedPlan;
      if (selectedPlan === 'standard' || selectedPlan === 'premium') {
        setSubscriptionLevel(selectedPlan);
      }
    }
  }, [location.state?.selectedPlan, setSubscriptionLevel]);
  
  // Use refs to control address loading behavior
  const hasInitialAddressCheck = useRef(false);
  const isComponentMounted = useRef(true);
  

  
  // Clear address flag when component unmounts - this ensures fresh loading on next mount
  useEffect(() => {
    isComponentMounted.current = true;
    
    return () => {
      isComponentMounted.current = false;
      hasInitialAddressCheck.current = false;
    };
  }, []);
  
  // Simple effect to handle address selection on component init
  useEffect(() => {
    // Skip if already loaded
    const token = localStorage.getItem('authToken');
    if (hasInitialAddressCheck.current) {
      return;
    }
    
    // Mark as checked to prevent duplicate processing
    hasInitialAddressCheck.current = true;
    
    // Only load addresses if user is logged in
    if (!token) {
      return;
    }
    
    // Prioritize selection with existing data before calling API
    if (addresses && addresses.length > 0 && !selectedAddress) {
      // Use existing data if available (avoid API call)
      const addressToSelect = selectBestAddressFrom(addresses);
      if (addressToSelect) {
        selectAddress(structuredClone(addressToSelect));
      }
    } else if (!addressLoading && !selectedAddress) {
      // Only if not already loading and no address selected
      refreshAddresses();
    }
  }, [addresses, selectedAddress, addressLoading, selectBestAddressFrom, 
      selectAddress, refreshAddresses]);
  
  // Use memoized values for default states to prevent re-renders
  const defaultTimeSlots = useMemo(() => 
    location.state?.selectedTimeSlots || ['9:00 AM - 12:00 PM', '2:00 PM - 5:00 PM'],
    [location.state?.selectedTimeSlots]
  );
  
  const defaultSelectedDate = useMemo(() => 
    typeof location.state?.selectedDate === 'string' ? location.state.selectedDate : '',
    [location.state?.selectedDate]
  );
  
  const defaultWeeklyDay = useMemo(() => 
    location.state?.weeklyDay !== undefined ? location.state.weeklyDay : 1,
    [location.state?.weeklyDay]
  );

  // Initialize schedule state from location state or defaults
  useEffect(() => {
    updateScheduleState({
      selectedTimeSlots: defaultTimeSlots,
      selectedDate: defaultSelectedDate,
      weeklyDay: defaultWeeklyDay
    });
  }, [defaultTimeSlots, defaultSelectedDate, defaultWeeklyDay, updateScheduleState]);

  // Get price calculation functionality from Zustand selector
  const { calculatePrice } = usePriceCalculation();
  
  // Get currency state
  const { currency, availableCurrencies } = useCurrencyState();
  
  // State to store the calculated total from OrderSummarySection
  const [calculatedTotal, setCalculatedTotal] = useState<number>(0);
  
  // State to store original pricing from config service
  const [originalRegularPricing, setOriginalRegularPricing] = useState<any>(null);
  const [trialOriginalPricing, setTrialOriginalPricing] = useState<any>(null);
  const [cryptoPricing, setCryptoPricing] = useState<any>(null);
  
  // State for new user gift eligibility
  const [isEligibleForGift, setIsEligibleForGift] = useState<boolean>(false);
  const [giftEligibilityChecked, setGiftEligibilityChecked] = useState<boolean>(false);
  
  // Promotion code hook - use this instead of directly managing state
  const {
    promotionCode: promoCodeFromHook,
    promotionDetails: promoDetailsFromHook,
    setPromotionCode: setPromoCodeFromHook,
    clearPromotionCode: clearPromoCodeFromHook,
    isPromotionCodeLoading,
    getEffectivePromotionCode,
    
    // Only keep these two properties
    hasValidDiscount,
    getEffectiveCode,
  } = usePromotionCode();

  // Using useMemo to stabilize the hook values and prevent re-rendering loops
  const promotionHookValues = useMemo(() => {
    return {
      promotionCode: promoCodeFromHook,
      promotionDetails: promoDetailsFromHook,
      setPromotionCode: setPromoCodeFromHook,
      clearPromotionCode: clearPromoCodeFromHook,
      isPromotionCodeLoading,
      getEffectivePromotionCode,
      
      // Only keep these two properties
      hasValidDiscount,
      getEffectiveCode
    }
  }, [
    promoCodeFromHook,
    promoDetailsFromHook,
    setPromoCodeFromHook,
    clearPromoCodeFromHook,
    isPromotionCodeLoading,
    getEffectivePromotionCode,
    hasValidDiscount,
    getEffectiveCode
  ]);

  // Use hook version of promo code handlers directly instead of state from store
  // Memoize the handler to prevent recreation on each render
  const onClearPromoCode = useCallback(() => {
    promotionHookValues.clearPromotionCode();
  }, [promotionHookValues]);


  const originalPrice = useMemo(() => {
    // For trial subscriptions, use trial pricing
    if (subscriptionType === 'trial') {
      const trialPrice = subscriptionLevel === 'premium' 
        ? (currency === 'usd' 
          ? (planDetails?.premium?.trial_price_usd || 8.99)
          : (planDetails?.premium?.trial_price || 299))
        : (currency === 'usd'
          ? (planDetails?.standard?.trial_price_usd || 5.99)
          : (planDetails?.standard?.trial_price || 199));
      return trialPrice;
    }
    
    // Special crypto pricing if payment method is crypto and currency is USD (monthly subscription only)
    if (selectedPaymentMethod === 'crypto' && currency === 'usd' && subscriptionType === 'monthly') {
      return getCryptoPriceFromConfig(subscriptionLevel, serviceType, cryptoPricing);
    }
    
    return subscriptionLevel === 'premium' 
      ? (planDetails?.premium?.price || 599 * 1.5)
      : (planDetails?.standard?.price || 599);
  }, [subscriptionLevel, planDetails, selectedPaymentMethod, currency, subscriptionType, serviceType, cryptoPricing]);
  
  // Calculate the final price using the selector's calculation function
  const finalTotal = useMemo(() => {
    if (!planDetails) return 599;
    
    // For trial subscriptions, use trial pricing directly (no additional discounts)
    if (subscriptionType === 'trial') {
      const trialPrice = subscriptionLevel === 'premium' 
        ? (currency === 'usd' 
          ? (planDetails?.premium?.trial_price_usd || 8.99)
          : (planDetails?.premium?.trial_price || 299))
        : (currency === 'usd'
          ? (planDetails?.standard?.trial_price_usd || 5.99)
          : (planDetails?.standard?.trial_price || 199));
      return trialPrice;
    }
    
    // Special crypto pricing if payment method is crypto and currency is USD (monthly subscription only)
    if (selectedPaymentMethod === 'crypto' && currency === 'usd' && subscriptionType === 'monthly') {
      return getCryptoPriceFromConfig(subscriptionLevel, serviceType, cryptoPricing);
    }
    
    const basePrice = subscriptionLevel === 'premium' 
      ? (planDetails.premium?.price || 599 * 1.5)
      : (planDetails.standard?.price || 599);
    
    // For yearly subscriptions, apply yearly pricing calculation directly
    if (subscriptionType === 'yearly') {
      const yearlyPrice = basePrice * 12;
      // Apply yearly discount based on payment type
      if (paymentType === 'subscription') {
        // Combined yearly + subscription discount: 15% total discount
        return yearlyPrice * 0.85;
      } else {
        // Only yearly discount: 10% discount
        return yearlyPrice * 0.9;
      }
    }
      
    return calculatePrice(basePrice);
  }, [planDetails, subscriptionLevel, calculatePrice, selectedPaymentMethod, currency, subscriptionType, serviceType, paymentType, cryptoPricing]);
  
  // Apply billing period discount if applicable
  const adjustedBasePrice = useMemo(() => {
    // For trial subscriptions, no additional discounts
    if (subscriptionType === 'trial') {
      return originalPrice;
    }
    
    // Special crypto pricing if payment method is crypto and currency is USD (monthly subscription only)
    if (selectedPaymentMethod === 'crypto' && currency === 'usd' && subscriptionType === 'monthly') {
      return getCryptoPriceFromConfig(subscriptionLevel, serviceType, cryptoPricing);
    }
    
    if (subscriptionType === 'yearly') {
      const yearlyPrice = originalPrice * 12;
      // Apply combined discount for yearly + subscription, or only yearly discount
      return paymentType === 'subscription' 
        ? yearlyPrice * 0.85 // Combined 15% discount
        : yearlyPrice * 0.9; // Only 10% yearly discount
    }
    
    return originalPrice;
  }, [subscriptionType, originalPrice, selectedPaymentMethod, subscriptionLevel, currency, serviceType, paymentType, cryptoPricing]);

  // Price after subscription payment discount (but before promotion)
  const priceBeforePromotion = useMemo(() => {
    // For trial subscriptions, no additional discounts
    if (subscriptionType === 'trial') {
      return originalPrice;
    }
    
    // Special crypto pricing if payment method is crypto and currency is USD (monthly subscription only)
    if (selectedPaymentMethod === 'crypto' && currency === 'usd' && subscriptionType === 'monthly') {
      return getCryptoPriceFromConfig(subscriptionLevel, serviceType, cryptoPricing);
    }
    
    // For yearly subscriptions, the adjustedBasePrice already includes the proper discount
    if (subscriptionType === 'yearly') {
      return adjustedBasePrice;
    }
    
    // For monthly subscriptions, apply subscription discount if applicable
    return paymentType === 'subscription'
      ? adjustedBasePrice * 0.95 // 5% subscription discount
      : adjustedBasePrice;
  }, [paymentType, adjustedBasePrice, selectedPaymentMethod, subscriptionLevel, currency, subscriptionType, originalPrice, serviceType, cryptoPricing]);

  // Add service type to the subscription store when it changes
  useEffect(() => {
    useSubscriptionStore.setState({ serviceType });
  }, [serviceType]);

  // Check new user gift eligibility when service type changes or user authentication changes
  useEffect(() => {
    const checkGiftEligibility = async () => {
      try {
        const eligible = await isEligibleForNewSubscriberGift(serviceType);
        setIsEligibleForGift(eligible);
        setGiftEligibilityChecked(true);
      } catch (error) {
        console.error('Error checking gift eligibility:', error);
        setIsEligibleForGift(false);
        setGiftEligibilityChecked(true);
      }
    };

    // Only check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (token && currentUser) {
      checkGiftEligibility();
    } else {
      setIsEligibleForGift(false);
      setGiftEligibilityChecked(true);
    }
  }, [serviceType, currentUser]);

  // Fetch original pricing configurations from config service
  useEffect(() => {
    const fetchOriginalPricing = async () => {
      try {
        const [regularPricing, trialPricing, cryptoPricingData] = await Promise.all([
          configService.getOriginalRegularPricing(serviceType),
          configService.getTrialOriginalPricing(serviceType),
          configService.getCryptoPricing(serviceType)
        ]);
        
        setOriginalRegularPricing(regularPricing);
        setTrialOriginalPricing(trialPricing);
        setCryptoPricing(cryptoPricingData);
      } catch (error) {
        console.error('Error fetching original pricing:', error);
        // Keep default null values so hardcoded fallbacks are used
      }
    };

    fetchOriginalPricing();
  }, [serviceType]);

  // Fetch subscription plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await subscriptionService.getPlans();
        
        // Ensure we have a valid plans data before proceeding
        if (!response) {
          console.error('Invalid plans data: received null or undefined');
          setError('Failed to load subscription plans. Please try again.');
          return;
        }
        
        // Get plans array from the response - handle different response formats
        const plansData: any[] = Array.isArray(response) ? response : 
                                (response as any).data && Array.isArray((response as any).data) ? 
                                (response as any).data : [];
        
        // Filter plans by service type
        const filteredPlans = plansData.filter((plan: any) => 
          !plan.service_type || plan.service_type === serviceType
        );
        
        // Fix the data structure: convert array to {standard: {...}, premium: {...}} format
        if (filteredPlans.length > 0) {
          const formattedPlans: PlanDetails = {
            standard: filteredPlans.find((plan: any) => plan.type === 'standard') || null,
            premium: filteredPlans.find((plan: any) => plan.type === 'premium') || null
          };
          setPlanDetails(formattedPlans);
          
          // Track view_item_list when plans are loaded
          try {
            const items = [
              {
                item_id: generateProductId(serviceType, 'standard', 'monthly'),
                item_name: generateProductName(serviceType, 'standard', 'monthly'),
                item_category: generateProductCategory(serviceType),
                item_brand: 'Piika',
                price: formattedPlans.standard?.price || 599,
                quantity: 1,
                index: 0
              },
              {
                item_id: generateProductId(serviceType, 'premium', 'monthly'),
                item_name: generateProductName(serviceType, 'premium', 'monthly'),
                item_category: generateProductCategory(serviceType),
                item_brand: 'Piika',
                price: formattedPlans.premium?.price || 999,
                quantity: 1,
                index: 1
              }
            ];

            analyticsService.trackViewItemList(
              items,
              `${serviceType} Subscription Plans`,
              `${serviceType}_plans_list`
            );

            console.log('📊 Tracked view_item_list for subscription plans:', { serviceType, items });
          } catch (analyticsError) {
            console.error('Failed to track subscription plans view:', analyticsError);
          }
        } else {
          // If no matching plans found, try without filtering
          const defaultPlans: PlanDetails = {
            standard: plansData.find((plan: any) => plan.type === 'standard') || null,
            premium: plansData.find((plan: any) => plan.type === 'premium') || null
          };
          setPlanDetails(defaultPlans);
          
          // Track view_item_list for default plans
          try {
            const items = [
              {
                item_id: generateProductId(serviceType, 'standard', 'monthly'),
                item_name: generateProductName(serviceType, 'standard', 'monthly'),
                item_category: generateProductCategory(serviceType),
                item_brand: 'Piika',
                price: defaultPlans.standard?.price || 599,
                quantity: 1,
                index: 0
              },
              {
                item_id: generateProductId(serviceType, 'premium', 'monthly'),
                item_name: generateProductName(serviceType, 'premium', 'monthly'),
                item_category: generateProductCategory(serviceType),
                item_brand: 'Piika',
                price: defaultPlans.premium?.price || 999,
                quantity: 1,
                index: 1
              }
            ];

            analyticsService.trackViewItemList(
              items,
              `${serviceType} Subscription Plans`,
              `${serviceType}_plans_list`
            );

            console.log('📊 Tracked view_item_list for subscription plans:', { serviceType, items });
          } catch (analyticsError) {
            console.error('Failed to track subscription plans view:', analyticsError);
          }
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setError('Failed to load subscription plans. Please try again.');
        // Don't set default plans - let the error show on the page
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlans();
  }, [setLoading, setError, setPlanDetails, serviceType]);

  // Validate plan type before sending to API - memoized
  const validatePlanType = useCallback((plan: string): boolean => {
    return VALID_PLAN_TYPES.includes(plan as PlanType);
  }, []);

  // Effect for payment method and payment type sync
  useEffect(() => {
    // For trial subscriptions, always use one-time payment
    if (subscriptionType === 'trial' && paymentType !== 'one_time') {
      startTransition(() => {
        updateSubscriptionState({ paymentType: 'one_time' });
      });
      return;
    }
    
    // When PromptPay is selected, set payment type to one_time and show info
    if (selectedPaymentMethod === 'promptpay') {
      if (paymentType !== 'one_time') {
        // Update payment type to one_time for PromptPay
        // Don't trigger another sync cycle if we're already setting
        startTransition(() => {
          updateSubscriptionState({ paymentType: 'one_time' });
          setShowPromptPayInfo(true);
        });
        
        // Auto-hide the info message after 4 seconds
        const timer = setTimeout(() => setShowPromptPayInfo(false), 4000);
        return () => clearTimeout(timer);
      }
    } 
    // Remove coupon validation from here as it's handled in fetchPromotionDetails
    // This check needs to happen after the PromptPay check to avoid race conditions
    else if (paymentType === 'subscription' && 
             selectedPaymentMethod !== 'card' && 
             selectedPaymentMethod !== 'crypto') { // Also allow crypto for subscription
      // For subscription payment type, only card and crypto are supported
      startTransition(() => {
        updateSubscriptionState({ paymentMethod: 'card' });
      });
    } else if (showPromptPayInfo) { // Only update if it needs to change
      setShowPromptPayInfo(false);
    }
  }, [paymentType, selectedPaymentMethod, showPromptPayInfo, updateSubscriptionState, setShowPromptPayInfo, subscriptionType]);

  // Reset toast flags when unmounting or when promotion code changes
  useEffect(() => {
    if (promotionCode) {
      // Reset flags when promotion code changes
      setHasShownPromoToast(false);
      setHasShownPaymentSwitchToast(false);
    }
    
    return () => {
      // Reset flags when component unmounts
      setHasShownPromoToast(false);
      setHasShownPaymentSwitchToast(false);
    };
  }, [promotionCode, setHasShownPromoToast, setHasShownPaymentSwitchToast]);

  // Handle contact support
  const handleContactSupport = useCallback(() => {
    // Open email client with support email
    window.open('mailto:support@piika.co?subject=Payment Issue - Subscription Creation', '_blank');
  }, []);

  // Handle dismiss error
  const handleDismissError = useCallback(() => {
    setPaymentError(null);
  }, []);

  // Handle remove promotion code for promotion code errors
  const handleRemovePromoCode = useCallback(() => {
    // Clear the promotion code using the hook
    promotionHookValues.clearPromotionCode();
    // Clear the error as well
    setPaymentError(null);
    // Show a success message
    toast.success(t('subscription.paymentErrors.promotionCodeRemoved'));
  }, [promotionHookValues, t]);

  const handleSubscribe = useCallback(async () => {
    // Check if subscription purchases are enabled
    if (!FEATURES.ENABLE_SUBSCRIPTION_PURCHASES) {
      toast.error(t('common.maintenance.title'));
      return;
    }

    // Validate user authentication
    const authValidation = validateUserAuthentication();
    if (!authValidation.isValid) return;

    // Reset error states
    setError(null);
    setPaymentError(null);
    
    // Debug logging for yearly subscription pricing issues
    if (subscriptionType === 'yearly') {
      console.log('🔍 Yearly Subscription Debug Info:', {
        subscriptionType,
        subscriptionLevel,
        paymentType,
        selectedPaymentMethod,
        currency,
        finalTotal,
        calculatedTotal,
        priceBeforePromotion,
        basePrice: subscriptionLevel === 'premium' 
          ? (planDetails?.premium?.price || 0)
          : (planDetails?.standard?.price || 0),
        planDetails: planDetails ? {
          standard: planDetails.standard,
          premium: planDetails.premium
        } : null
      });
    }
    
    // Validate address
    const addressValidation = validateAddress(selectedAddress, setShowAddressModal);
    if (!addressValidation.isValid) return;
    
    // Validate gift information
    const giftValidation = validateGiftInformation(isGift, senderName, senderPhone);
    if (!giftValidation.isValid) return;
    
    try {
      setLoading(true);
      toast.loading(t('subscription.loading.creatingSubscription'));
      
      // Track analytics events
      await trackBeginCheckout(planDetails, subscriptionLevel, subscriptionType, serviceType, currency);
      await trackInitiateCheckout(currency, priceBeforePromotion, serviceType, subscriptionLevel, subscriptionType);
      
      // Build subscription parameters
      const plan: PlanType = subscriptionLevel === 'premium' ? 'premium' : 'standard';
      
      // Validate plan type
      if (!validatePlanType(plan)) {
        setError(t('subscription.errors.invalidSubscriptionPlan', { message: `Invalid subscription plan: ${plan}` }));
        setLoading(false);
        toast.error(t('subscription.errors.invalidSubscriptionPlan', { message: `Invalid subscription plan: ${plan}` }));
        return;
      }
      
      
      try {
        // Create subscription using the hook
        const subscription = await createSubscription({
          plan,
          serviceType,
          isGift,
          selectedAddress: selectedAddress!,
          subscriptionType,
          weeklyDay,
          selectedTimeSlots,
          currency,
          getEffectiveCode,
          recipientName,
          recipientPhone,
          giftMessage,
          senderName,
          senderPhone
        });
        
        // Track subscription events
        await trackSubscriptionEvents(
          currency,
          calculatedTotal,
          subscription.id,
          serviceType,
          subscriptionLevel,
          subscriptionType,
          selectedPaymentMethod,
          promotionCode
        );
        
        // Handle promotion code
        const effectivePromotionCode = await handlePromotionCode({
          promotionCode,
          promotionDetails,
          priceBeforePromotion,
          getEffectivePromotionCode,
          paymentType
        });
        
        // Process payment using the hook
        await processPayment({
          subscription,
          paymentType,
          selectedPaymentMethod,
          subscriptionLevel,
          subscriptionType,
          currency,
          serviceType,
          planDetails,
          selectedAddress: selectedAddress!,
          isGift,
          calculatedTotal,
          finalTotal,
          effectivePromotionCode,
          promotionDetails,
          cryptoPricing,
          currentUserEmail: currentUser?.email,
          recipientName,
          recipientPhone,
          giftMessage,
          senderName,
          senderPhone
        });
      } catch (error) {
        console.error('Subscription creation error:', error);
        
        // Clear any previous errors
        setError(null);
        
        // Format and set the payment error for detailed display
        const errorInfo = formatErrorForDisplay(error);
        setPaymentError(error);
        
        // Show toast with formatted error message
        toast.error(`${errorInfo.title}: ${errorInfo.message}`);
      }
    } catch (error) {
      console.error('Overall error:', error);
      
      // Clear any previous errors
      setError(null);
      
      // Format and set the payment error for detailed display
      const errorInfo = formatErrorForDisplay(error);
      setPaymentError(error);
      
      // Show toast with formatted error message
      toast.error(`${errorInfo.title}: ${errorInfo.message}`);
    } finally {
      setLoading(false);
    }
  }, [
    validateUserAuthentication, validateAddress, validateGiftInformation,
    trackBeginCheckout, trackInitiateCheckout, trackSubscriptionEvents,
    createSubscription, handlePromotionCode, processPayment,
    selectedAddress, isGift, recipientName, recipientPhone, giftMessage,
    senderName, senderPhone, validatePlanType, subscriptionLevel, 
    weeklyDay, selectedTimeSlots, paymentType, 
    selectedPaymentMethod, subscriptionType, calculatedTotal, 
    currentUser?.email, promotionCode, promotionDetails,
    priceBeforePromotion, setError, setLoading,
    currency, serviceType, getEffectiveCode, cryptoPricing,
    planDetails, finalTotal, getEffectivePromotionCode
  ]);

  // Handle retry payment - defined after handleSubscribe to avoid circular dependency
  const handleRetryPayment = useCallback(async () => {
    setIsRetrying(true);
    setPaymentError(null);
    
    try {
      // Re-execute the subscription logic
      await handleSubscribe();
    } finally {
      setIsRetrying(false);
    }
  }, [handleSubscribe]);

  // Function to handle address update using modal instead of navigation
  const navigateToAddressPage = useCallback(() => {
    // Check if user is logged in first
    const token = localStorage.getItem('authToken');
    if (!token) {
      const currentPath = window.location.pathname + window.location.search;
      navigate(`/register?returnTo=${encodeURIComponent(currentPath)}`, { replace: true });
      return;
    }

    // If addresses exist, select the default address
    if (addresses && addresses.length > 0 && !selectedAddress) {
      const defaultAddress = selectBestAddressFrom(addresses);
      if (defaultAddress) {
        selectAddress(structuredClone(defaultAddress));
        if (document.visibilityState === 'visible') {
          toast.success(t('subscription.toasts.addressSelected'));
        }
      }
    }
    
    // Always show modal when address section is clicked
    setShowAddressModal(true);
  }, [setShowAddressModal, addresses, selectedAddress, selectBestAddressFrom, selectAddress, t, navigate]);

  // Add a handler for schedule updates
  const handleScheduleUpdate = useCallback((scheduleData: any) => {
    // Check if there are actual changes before updating
    const hasTimeSlotChanges = 
      scheduleData.selectedTimeSlots && 
      JSON.stringify(scheduleData.selectedTimeSlots) !== JSON.stringify(selectedTimeSlots);
    
    const hasDateChange = 
      scheduleData.selectedDate && scheduleData.selectedDate !== selectedDate;
    
    const hasWeekDayChange = 
      scheduleData.weeklyDay !== undefined && scheduleData.weeklyDay !== weeklyDay;
    
    // Only update if there are actual changes
    if (hasTimeSlotChanges || hasDateChange || hasWeekDayChange) {
      // Update the component state variables
      startTransition(() => {
        updateScheduleState({
          selectedTimeSlots: scheduleData.selectedTimeSlots || selectedTimeSlots,
          selectedDate: scheduleData.selectedDate instanceof Date ? 
            scheduleData.selectedDate.toISOString() : 
            scheduleData.selectedDate || selectedDate,
          weeklyDay: scheduleData.weeklyDay !== undefined ? 
            scheduleData.weeklyDay : weeklyDay
        });
        
        // Show success toast
        toast.success(t('subscription.toasts.deliveryScheduleUpdated'));
      });
    }
  }, [updateScheduleState, selectedTimeSlots, selectedDate, weeklyDay, t]);

  // Handle closing the address modal
  const handleCloseAddressModal = useCallback(() => {
    if (!selectedAddress && addresses.length > 0) {
      // If user closes without selecting but addresses are available,
      // show a warning and select the first/default one automatically
      toast.error(t('subscription.toasts.addressError'));
      
      // Use our helper function to select the best address
      const addressToSelect = selectBestAddressFrom(addresses);
      
      if (addressToSelect) {
        startTransition(() => {
          selectAddress(structuredClone(addressToSelect));
          toast.success(t('subscription.toasts.addressAutoSelected'));
        });
      }
    }
    
    setShowAddressModal(false);
  }, [selectedAddress, addresses, selectBestAddressFrom, selectAddress, setShowAddressModal, t]);

  // Handle address selection from the modal
  const handleAddressSelected = useCallback((address: AddressWithId) => {
    // Create a reference copy to avoid reference issues
    const addressRef = structuredClone(address);
    
    // Mark that we've attempted address selection
    addressSelectionAttempted.current = true;
    
    // Wrap in startTransition to make this a lower priority update
    startTransition(() => {
      // Update the selected address using the context
      selectAddress(addressRef);
      
      // Close the modal and show success message
      setShowAddressModal(false);
      // Also hide the manual address prompt
      setShowManualAddressPrompt(false);
      toast.success(t('subscription.toasts.deliveryAddressUpdated'));
    });
  }, [selectAddress, setShowAddressModal, setShowManualAddressPrompt, t]);

  // Handle navigation intent from Address page
  useEffect(() => {
    const returnedIntent = location.state?.navigationIntent;
    
    if (returnedIntent === 'select_delivery_address') {
      if (location.state?.addressUpdated) {
        toast.success(t('subscription.toasts.deliveryAddressUpdated'));
      }
    } else if (returnedIntent === 'validate_address_for_checkout') {
      if (location.state?.selectedAddress) {
        toast.success(t('subscription.toasts.addressConfirmed'));
      }
    }
    
    // Handle returning from schedule setup - use store state to prevent duplicate toasts
    if (location.state?.scheduleUpdated && !hasShownScheduleToast) {
      toast.success(t('subscription.toasts.deliveryScheduleUpdated'));
      setHasShownScheduleToast(true);
    }
  }, [location.state?.navigationIntent, location.state?.addressUpdated, location.state?.scheduleUpdated, hasShownScheduleToast, setHasShownScheduleToast, t]);

  // Reset the toast shown flag when location changes
  useEffect(() => {
    // Reset the state when location changes
    return () => {
      setHasShownScheduleToast(false);
    };
  }, [location.key, setHasShownScheduleToast]);



  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <GiftHeader 
        isGift={isGift} 
        title={isGift ? t('subscription.page.giftTitle') : t('subscription.page.title')}
        showBackButton={true}
      />

      {/* Main Content */}
      <div className="max-w-md mx-auto px-2 py-2 pb-28">   
        {/* Shutdown Notice */}
        <ShutdownNotice className="mb-4" />
        
        {/* Service Type Indicator */}
        <ServiceTypeSection 
          serviceType={serviceType}
          setServiceType={setServiceType}
        />

        {/* Plan Selection */}
        <div className="bg-white mt-2 rounded-xl overflow-hidden shadow-sm">
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-center">
              <Gift size={20} className="text-gray-500 mr-2" />
              <span className="text-base font-medium text-gray-900">{t('subscription.plansSection.planSelection')}</span>
            </div>
          </div>
          <div className="px-5 pb-4">
            <SubscriptionPlanSection
              subscriptionType={subscriptionType}
              setSubscriptionType={setSubscriptionType}
              subscriptionLevel={subscriptionLevel}
              setSubscriptionLevel={setSubscriptionLevel}
              planDetails={planDetails}
              loading={loading}
              error={error}
              vaseImage={vaseImage}
              displayMode="plan-level"
              paymentType={paymentType}
              setPaymentType={setPaymentType}
              selectedPaymentMethod={selectedPaymentMethod}
              serviceType={serviceType}
              promotionCode={promotionHookValues.promotionCode}
              promotionDetails={promotionHookValues.promotionDetails}
              getEffectivePromotionCode={promotionHookValues.getEffectivePromotionCode}
            />
          </div>
          
          {/* Subscription Information */}
          <div className="px-5 pb-3 text-xs text-gray-600 bg-gray-50 rounded-md mt-2">
            <div className="py-2">
              <p className="mb-1"><strong>{t('subscription.plansSection.importantInfo')}</strong></p>
              <ul className="list-disc pl-4 space-y-1">
                <li>
                  {serviceType === 'flower'
                    ? t('subscription.plansSection.monthlyInfo')
                    : t('subscription.plansSection.monthlyFruitInfo', 'Details about monthly fruit subscriptions, billed every 4 weeks, cancel or pause anytime.')}
                </li>
                <li>
                  {serviceType === 'flower'
                    ? t('subscription.plansSection.yearlyInfo')
                    : t('subscription.plansSection.yearlyFruitInfo', 'Details about yearly fruit subscriptions, offering a discount, billed annually, cancel or pause anytime.')}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* New User Gift Section */}
        {giftEligibilityChecked && (
          <NewUserGiftSection 
            serviceType={serviceType}
            isEligible={isEligibleForGift}
            subscriptionType={subscriptionType}
            className="mt-2"
          />
        )}

        {/* Payment Methods */}
        <div className="bg-white mt-2 px-5 py-4 rounded-xl shadow-sm mb-2">
          <PaymentMethodsSection 
            selectedMethod={selectedPaymentMethod}
            onSelect={setPaymentMethod}
            trackingData={{
              subscriptionLevel,
              subscriptionType,
              serviceType,
              currency,
              price: subscriptionType === 'trial' 
                ? (subscriptionLevel === 'premium' 
                  ? (planDetails?.premium?.trial_price || 299)
                  : (planDetails?.standard?.trial_price || 199))
                : (subscriptionLevel === 'premium' 
                  ? (planDetails?.premium?.price || 999)
                  : (planDetails?.standard?.price || 599))
            }}
          />
          
          {/* PromptPay info message */}
          {showPromptPayInfo && (
            <div className="mt-2 p-2 bg-blue-50 rounded-md flex items-start">
              <AlertCircle size={16} className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                {t('subscription.paymentMethods.promptpayInfo')}
              </p>
            </div>
          )}
          
          {/* Crypto special pricing message */}
          {selectedPaymentMethod === 'crypto' && currency === 'usd' && subscriptionType === 'monthly' && (
            <div className="mt-2 p-2 bg-green-50 rounded-md flex items-start">
              <Gift size={16} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-xs text-green-700">
                {serviceType === 'fruit'
                  ? t('subscription.paymentMethods.cryptoSpecialPricingFruit', { standardPrice: getCryptoPriceFromConfig('standard', serviceType, cryptoPricing), premiumPrice: getCryptoPriceFromConfig('premium', serviceType, cryptoPricing) })
                  : t('subscription.paymentMethods.cryptoSpecialPricing')}
              </p>
            </div>
          )}
        </div>
        
        {/* Delivery & Schedule Group */}
        {!isGift && (
          <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-2">
            {/* Check if user is logged in */}
            {!localStorage.getItem('authToken') ? (
              /* Show login prompt for address selection */
              <div className="p-5">
                <div className="flex items-center mb-3">
                  <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                    <span className="text-xs text-gray-600">📍</span>
                  </div>
                  <span className="text-base font-medium text-gray-900">{t('subscription.address.title', 'Delivery Address')}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      {t('subscription.address.loginRequired', 'Please login to select your delivery address')}
                    </p>
                    <button
                      onClick={navigateToAddressPage}
                      className="text-yellow-600 text-sm font-medium hover:text-yellow-700"
                    >
                      {t('subscription.address.loginButton', 'Login to Continue')}
                    </button>
                  </div>
                </div>
              </div>
            ) : addressLoading ? (
              /* Loading indicator for address */
              <div className="p-5 flex items-center justify-center">
                <div className="animate-pulse flex space-x-4 w-full">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-3 py-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ) : (
              /* Combined Address and Schedule section with stable rendering */
              <OptimizedAddressSection 
                selectedAddress={selectedAddress}
                selectedTimeSlots={selectedTimeSlots}
                selectedDate={selectedDate}
                weeklyDay={weeklyDay}
                isGift={isGift}
                showAddressOnly={false}
                showScheduleOnly={false}
                onClick={navigateToAddressPage}
                onScheduleUpdate={handleScheduleUpdate}
                showScheduleModal={true}
                trackingData={{
                  subscriptionLevel,
                  subscriptionType,
                  serviceType,
                  currency,
                  price: subscriptionType === 'trial' 
                    ? (subscriptionLevel === 'premium' 
                      ? (planDetails?.premium?.trial_price || 299)
                      : (planDetails?.standard?.trial_price || 199))
                    : (subscriptionLevel === 'premium' 
                      ? (planDetails?.premium?.price || 999)
                      : (planDetails?.standard?.price || 599))
                }}
              />
            )}
          </div>
        )}
        
        {/* 3. Recipient Information - styled differently for gift subscriptions */}
        {isGift && (
          <div className="bg-white mt-2 px-5 py-4 rounded-xl shadow-sm">
            {/* Check if user is logged in */}
            {!localStorage.getItem('authToken') ? (
              /* Show login prompt for gift subscription */
              <div>
                <div className="flex items-center mb-3">
                  <Gift size={20} className="text-gray-500 mr-2" />
                  <span className="text-base font-medium text-gray-900">{t('subscription.gift.recipientInfo', 'Recipient Information')}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      {t('subscription.gift.loginRequired', 'Please login to complete your gift subscription')}
                    </p>
                    <button
                      onClick={navigateToAddressPage}
                      className="text-yellow-600 text-sm font-medium hover:text-yellow-700"
                    >
                      {t('subscription.gift.loginButton', 'Login to Continue')}
                    </button>
                  </div>
                </div>
              </div>
            ) : addressLoading ? (
              /* Loading indicator for address in gift mode */
              <div className="flex items-center justify-center my-4">
                <div className="animate-pulse flex space-x-4 w-full">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-3 py-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ) : (
              <RecipientInfoSection
                recipientName={recipientName}
                setRecipientName={(val) => updateGiftState({ recipientName: val })}
                recipientPhone={recipientPhone}
                setRecipientPhone={(val) => updateGiftState({ recipientPhone: val })}
                giftMessage={giftMessage}
                setGiftMessage={(val) => updateGiftState({ giftMessage: val })}
                senderName={senderName}
                setSenderName={(val) => updateGiftState({ senderName: val })}
                senderPhone={senderPhone}
                setSenderPhone={(val) => updateGiftState({ senderPhone: val })}
                isGift={true}
                selectedAddress={selectedAddress}
                selectedTimeSlots={selectedTimeSlots}
                selectedDate={selectedDate}
                weeklyDay={weeklyDay}
                navigateToAddressPage={navigateToAddressPage}
                onScheduleUpdate={handleScheduleUpdate}
              />
            )}
          </div>
        )}



        {/* Currency Section */}
        <div className="bg-white mt-2 rounded-xl overflow-hidden shadow-sm">
          <div className="px-5 py-4">
            <CurrencySection />
          </div>
        </div>

        {/* Billing Period Selection */}
        <BillingPeriodSection
          subscriptionType={subscriptionType}
          setSubscriptionType={setSubscriptionType}
          subscriptionLevel={subscriptionLevel}
          setSubscriptionLevel={setSubscriptionLevel}
          planDetails={planDetails}
          loading={loading}
          error={error}
          vaseImage={vaseImage}
          paymentType={paymentType}
          setPaymentType={setPaymentType}
          selectedPaymentMethod={selectedPaymentMethod}
          serviceType={serviceType}
          promotionCode={promotionHookValues.promotionCode}
          promotionDetails={promotionHookValues.promotionDetails}
          getEffectivePromotionCode={promotionHookValues.getEffectivePromotionCode}
        />



        {/* 8. Order Summary */}
        <div className="bg-white mt-2 px-5 py-4 rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ShoppingCart size={20} className="text-gray-500 mr-2" />
              <span className="text-base font-medium text-gray-900">{t('subscription.orderSummary.title')}</span>
            </div>
          </div>
          <OrderSummarySection
            subscriptionLevel={subscriptionLevel}
            subscriptionType={subscriptionType as 'monthly' | 'yearly' | 'trial'}
            paymentType={paymentType}
            currentBasePriceForLevel={
              subscriptionType === 'trial'
                ? (subscriptionLevel === 'premium' 
                  ? (currency === 'usd' 
                    ? (planDetails?.premium?.trial_price_usd || getTrialPriceFromConfig('premium', 'usd', serviceType))
                    : (planDetails?.premium?.trial_price || getTrialPriceFromConfig('premium', 'thb', serviceType)))
                  : (currency === 'usd'
                    ? (planDetails?.standard?.trial_price_usd || getTrialPriceFromConfig('standard', 'usd', serviceType))
                    : (planDetails?.standard?.trial_price || getTrialPriceFromConfig('standard', 'thb', serviceType))))
                : (subscriptionLevel === 'premium' ? (planDetails?.premium?.price || 0) : (planDetails?.standard?.price || 0))
            }
            currentBasePriceForLevel_usd={
              subscriptionType === 'trial'
                ? (subscriptionLevel === 'premium' 
                  ? (planDetails?.premium?.trial_price_usd || getTrialPriceFromConfig('premium', 'usd', serviceType))
                  : (planDetails?.standard?.trial_price_usd || getTrialPriceFromConfig('standard', 'usd', serviceType)))
                : (subscriptionLevel === 'premium' ? planDetails?.premium?.price_usd : planDetails?.standard?.price_usd)
            }
            originalRegularPriceForLevel={
              subscriptionType === 'trial'
                ? getOriginalPriceFromConfig(subscriptionType, subscriptionLevel, currency, serviceType, originalRegularPricing, trialOriginalPricing)
                : (subscriptionLevel === 'premium' ? (planDetails?.premium?.original_price || getOriginalPriceFromConfig(subscriptionType, subscriptionLevel, currency, serviceType, originalRegularPricing, trialOriginalPricing)) : (planDetails?.standard?.original_price || getOriginalPriceFromConfig(subscriptionType, subscriptionLevel, currency, serviceType, originalRegularPricing, trialOriginalPricing)))
            }
            originalRegularPriceForLevel_usd={
              getOriginalPriceFromConfig(subscriptionType, subscriptionLevel, 'usd', serviceType, originalRegularPricing, trialOriginalPricing)
            }
            promotionCode={promoCodeFromHook}
            promotionDetails={promoDetailsFromHook}
            selectedPaymentMethod={selectedPaymentMethod}
            onClearPromoCode={onClearPromoCode}
            onTotalCalculated={setCalculatedTotal}
            serviceType={serviceType}
            showNewUserGift={isEligibleForGift}
            vaseImage={vaseImage}
            scissorsImage={scissorsImage}
          />
          
          {/* Subscription Details Info */}
          <SubscriptionDetailsInfo
            subscriptionType={subscriptionType}
            calculatedTotal={calculatedTotal}
            currency={currency}
            availableCurrencies={availableCurrencies}
          />
        </div>

        {/* FAQ Section */}
        <FAQSection serviceType={serviceType} />
        
        {/* Payment Error Display */}
        {paymentError && (
          <div className="mt-6 mx-4">
            <PaymentErrorDisplay
              error={paymentError}
              onRetry={handleRetryPayment}
              onContactSupport={handleContactSupport}
              onDismiss={handleDismissError}
              onRemovePromoCode={handleRemovePromoCode}
              isRetrying={isRetrying}
            />
          </div>
        )}
        
        {/* Subscribe Button - Default is fixed for all browsers */}
        <div ref={normalButtonRef} className="mt-6 mb-16">
          <SubscribeButton
            onClick={handleSubscribe}
            loading={loading}
            disabled={loading} // Original disabled logic
            isGift={isGift}
            paymentType={paymentType}
            fixed={true} /* Use fixed by default */
          />
        </div>
      </div>

      {/* Non-fixed button for Facebook browsers - only shown when fixed button is not visible */}
      {isFacebookBrowser && !isButtonVisible && (
        <div className="sticky bottom-4 mx-auto px-4 max-w-md z-40">
          <SubscribeButton
            onClick={handleSubscribe}
            loading={loading}
            disabled={loading} // Original disabled logic
            isGift={isGift}
            paymentType={paymentType}
            fixed={false} /* Use non-fixed as fallback when regular button isn't visible */
            className="shadow-lg rounded-lg" /* Add shadow for emphasis */
          />
        </div>
      )}

      {/* Manual address selection prompt */}
      <AddressSelectionPrompt 
        showManualAddressPrompt={showManualAddressPrompt}
        onSelectAddress={() => setShowAddressModal(true)}
      />

      {/* Only render AddressManagementModal when it's actually open and explicitly requested */}
      {showAddressModal && (
        <AddressManagementModal
          isOpen={showAddressModal}
          onClose={handleCloseAddressModal}
          onAddressSelect={handleAddressSelected}
          selectedAddress={selectedAddress}
          isSubscription={true}
        />
      )}
    </div>
  );
} 